/**
 * Route Actions Helpers
 * Shared utilities for React Router action functions
 */

// Type definitions
export interface ActionError {
  field?: string;
  message: string;
}

export interface ActionResponse<T = unknown> {
  success?: boolean;
  data?: T;
  errors?: Record<string, string>;
  error?: string;
  message?: string;
}

/**
 * Helper to create error response
 * Returns a Response object with JSON data
 */
export function actionError(
  errors: Record<string, string> | string,
  status: number = 400
): Response {
  if (typeof errors === 'string') {
    return new Response(JSON.stringify({ error: errors, success: false }), {
      status,
      headers: { 'Content-Type': 'application/json' },
    });
  }
  return new Response(JSON.stringify({ errors, success: false }), {
    status,
    headers: { 'Content-Type': 'application/json' },
  });
}

/**
 * Helper to create success response
 * Returns a Response object with JSON data
 */
export function actionSuccess<T>(data?: T, message?: string): Response {
  return new Response(JSON.stringify({ success: true, data, message }), {
    status: 200,
    headers: { 'Content-Type': 'application/json' },
  });
}

/**
 * Extract form data as object
 */
export async function getFormDataObject(request: Request): Promise<Record<string, string>> {
  const formData = await request.formData();
  const data: Record<string, string> = {};

  for (const [key, value] of formData.entries()) {
    data[key] = value.toString();
  }

  return data;
}

/**
 * Email validation
 */
export function validateEmail(email: string): string | null {
  if (!email) return 'Email is required';
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
    return 'Please enter a valid email address';
  }
  return null;
}

/**
 * Password validation
 */
export function validatePassword(password: string): string | null {
  if (!password) return 'Password is required';
  if (password.length < 8) return 'Password must be at least 8 characters';
  if (!/(?=.*[a-z])(?=.*[A-Z])/.test(password)) {
    return 'Password must contain uppercase and lowercase letters';
  }
  if (!/(?=.*\d)/.test(password)) {
    return 'Password must contain at least one number';
  }
  if (!/(?=.*[^a-zA-Z\d])/.test(password)) {
    return 'Password must contain at least one special character';
  }
  return null;
}

/**
 * Transform API errors to action errors
 */
export function transformApiError(error: unknown): ActionResponse {
  if (error && typeof error === 'object' && 'response' in error) {
    const axiosError = error as {
      response?: {
        data?: {
          errors?: Record<string, string>;
          message?: string;
        };
      };
    };
    const errorData = axiosError.response?.data;

    if (errorData?.errors) {
      // Field-level errors
      return { errors: errorData.errors, success: false };
    }

    if (errorData?.message) {
      return { error: errorData.message, success: false };
    }
  }

  return { error: 'An unexpected error occurred', success: false };
}
